import { useParams, Link } from 'react-router-dom';
import Metadata from '../components/Metadata';
import { services } from '../data/services';
import { getOptimizedImageUrl } from '../lib/utils';

export default function ServiceDetail() {
  const { serviceId } = useParams();

  // Find the current service
  const service = services.find((s) => s.id === serviceId);

  // Fallback if service is not found
  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <i className="fa-regular fa-triangle-exclamation text-5xl text-amber-500 mb-4" />
        <h1 className="text-2xl font-bold text-slate-900">Ydelsen blev ikke fundet</h1>
        <p className="mt-2 text-slate-500 max-w-md">
          Vi kunne desværre ikke finde den specifikke ydelse, du leder efter. Se alle vores ydelser på oversigten.
        </p>
        <Link
          to="/jeg-tilbyder"
          className="mt-6 inline-flex items-center justify-center rounded bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          Se alle ydelser
        </Link>
      </div>
    );
  }

  // Get other services for navigation/sidebar
  const otherServices = services.filter((s) => s.id !== serviceId);

  return (
    <>
      <Metadata
        title={`${service.title} - K. Pheiffer Construction`}
        description={service.shortDescription}
      />

      {/* Breadcrumbs & Page Header */}
      <section className="relative text-white py-24 sm:py-32 overflow-hidden bg-slate-950">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-500 scale-105"
          style={{ backgroundImage: `url(${getOptimizedImageUrl(service.image, 1600, 90)})` }}
        />
        <div className="absolute inset-0 bg-black/75 backdrop-blur-[1px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-black/40" />
        
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Forside</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" />
            <Link to="/jeg-tilbyder" className="hover:text-white transition-colors">Jeg tilbyder</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" />
            <span className="text-emerald-400 font-medium truncate">{service.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                {service.title}
              </h1>
            </div>
            <div>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 text-sm font-semibold tracking-wide transition-all duration-200 active:scale-95"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Left/Main Column: Service Details */}
            <div className="lg:col-span-8 space-y-8">
              <div className="relative aspect-[16/10] sm:aspect-[21/9] lg:aspect-[16/10] rounded-2xl overflow-hidden shadow-md bg-slate-100">
                <img
                  src={getOptimizedImageUrl(service.image, 1200, 90)}
                  alt={service.title}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="prose prose-slate max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Om ydelsen</h2>
                <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                  {service.description}
                </p>
                <p className="text-slate-600 text-base leading-relaxed mt-4">
                  Hos K. Pheiffer Construction går vi aldrig på kompromis med kvaliteten. Vi anvender udelukkende gennemtestede materialer fra anerkendte leverandører, og vi sørger altid for at holde os opdateret med de nyeste teknikker og standarder inden for byggebranchen. Det sikrer dig et resultat, der ikke bare er smukt i dag, men som også holder i mange år fremover.
                </p>
              </div>

              {/* Benefits Section */}
              <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="fa-regular fa-shield-check text-emerald-600" />
                  Hvorfor vælge os til denne opgave?
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                      <i className="fa-regular fa-circle-check text-emerald-600 mt-1 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact CTA Block */}
              <div className="bg-slate-950 text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden border border-slate-800">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(123,101,61,0.1),transparent_40%)]" />
                <div className="relative z-10 max-w-2xl">
                  <h3 className="text-xl sm:text-2xl font-bold">Skal vi hjælpe dig med dit projekt?</h3>
                  <p className="mt-2 text-slate-400 text-sm sm:text-base">
                    Kontakt os i dag for en uforpligtende snak om dine ønsker og behov. Vi udarbejder gerne et skræddersyet og gennemskueligt tilbud på din opgave.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-4">
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-3 text-sm font-semibold transition-colors"
                    >
                      Kontakt os nu
                    </Link>
                    <a
                      href="tel:+4560183562"
                      className="inline-flex items-center justify-center rounded border border-slate-700 hover:border-white text-white px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap"
                    >
                      <i className="fa-regular fa-phone mr-2" />
                      Ring 60 18 35 62
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Sidebar */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-8 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2 scrollbar-thin">
                {/* Other Services Navigation */}
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <h3 className="text-base font-bold text-slate-900 uppercase tracking-wider mb-4">
                    Andre ydelser
                  </h3>
                  <div className="space-y-2">
                    {otherServices.map((s) => (
                      <Link
                        key={s.id}
                        to={`/jeg-tilbyder/${s.id}`}
                        className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-100 hover:border-emerald-500/30 hover:shadow-sm transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded bg-slate-50 text-slate-600 flex items-center justify-center group-hover:bg-emerald-500/10 group-hover:text-emerald-600 transition-colors">
                            <i className={`${s.icon} text-xs`} />
                          </div>
                          <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">
                            {s.title.split(' & ')[0].split(' af ')[0]}
                          </span>
                        </div>
                        <i className="fa-regular fa-chevron-right text-xs text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Direct Contact Card */}
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 space-y-4">
                  <h3 className="text-base font-bold text-slate-900 uppercase tracking-wider">
                    Direkte kontakt
                  </h3>
                  <p className="text-sm text-slate-500">
                    Har du spørgsmål til vores ydelser eller priser? Vi svarer altid hurtigt på både telefon og e-mail.
                  </p>
                  <div className="space-y-3 pt-2">
                    <a
                      href="tel:+4560183562"
                      className="flex items-center gap-3 text-slate-700 hover:text-emerald-600 transition-colors group"
                    >
                      <div className="h-8 w-8 rounded bg-white border border-slate-100 flex items-center justify-center text-emerald-600">
                        <i className="fa-regular fa-phone text-xs" />
                      </div>
                      <span className="text-sm font-medium whitespace-nowrap">60 18 35 62</span>
                    </a>
                    <a
                      href="mailto:kontakt@kpheiffer.dk"
                      className="flex items-center gap-3 text-slate-700 hover:text-emerald-600 transition-colors group"
                    >
                      <div className="h-8 w-8 rounded bg-white border border-slate-100 flex items-center justify-center text-emerald-600">
                        <i className="fa-regular fa-envelope text-xs" />
                      </div>
                      <span className="text-sm font-medium whitespace-nowrap">kontakt@kpheiffer.dk</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
